import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { quotationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import HomeButton from '../components/HomeButton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Download, CheckCircle2, XCircle, Package,
  FileText, Loader2, Clock, Send, AlertTriangle, Plus, Pencil, Save, Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';

const STATUSES = ['Pending', 'In Progress', 'Quotation Ready', 'Sent to Customer', 'Confirmed', 'Order Raised', 'Rejected'];
const URGENCY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];
const DIAMOND_QUALITIES = ['VVS EF', 'GH SI', 'VSSI GH', 'SI-IJ', 'VVSVS GH', 'VVS FG'];
const GOLD_KARATS = ['9kt', '14kt', '18kt'];

const statusConfig = {
  'Pending': { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400', icon: Clock },
  'In Progress': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400', icon: Loader2 },
  'Quotation Ready': { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-400', icon: FileText },
  'Sent to Customer': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400', icon: Send },
  'Confirmed': { color: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400', icon: CheckCircle2 },
  'Order Raised': { color: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400', icon: Package },
  'Rejected': { color: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400', icon: XCircle },
};

const urgencyConfig = {
  'Low': 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400',
  'Medium': 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
  'High': 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400',
  'Urgent': 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
};

const statusSteps = ['Pending', 'In Progress', 'Quotation Ready', 'Sent to Customer', 'Confirmed', 'Order Raised'];

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return '-'; }
}

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchQuotation = async () => {
    try {
      const res = await quotationsApi.getById(id);
      setQuotation(res.data);
    } catch (err) {
      toast.error('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotation(); }, [id]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await quotationsApi.upload(id, file);
      toast.success('Quotation file uploaded successfully');
      fetchQuotation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await quotationsApi.download(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = quotation.quotation_file_name || 'quotation.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setActionLoading(newStatus);
    try {
      await quotationsApi.update(id, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchQuotation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading('');
    }
  };

  const handleConfirm = async () => {
    setActionLoading('confirm');
    try {
      await quotationsApi.confirm(id);
      toast.success('Quotation confirmed!');
      fetchQuotation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async () => {
    setActionLoading('reject');
    try {
      await quotationsApi.reject(id);
      toast.success('Quotation rejected');
      fetchQuotation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading('');
    }
  };

  const handleCreateOrder = async () => {
    setActionLoading('create-order');
    try {
      await quotationsApi.createOrder(id);
      toast.success('Quotation marked as Order Raised. Navigate to Orders to create the production order.');
      fetchQuotation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this quotation request?')) return;
    setActionLoading('delete');
    try {
      await quotationsApi.delete(id);
      toast.success('Quotation deleted');
      navigate('/quotations');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
      setActionLoading('');
    }
  };

  const startEditing = () => {
    setEditData({
      customer_name: quotation.customer_name,
      diamond_quality: quotation.diamond_quality,
      gold_karat: quotation.gold_karat,
      urgency: quotation.urgency,
      special_instructions: quotation.special_instructions,
      internal_notes: quotation.internal_notes || '',
      products: quotation.products.length > 0 ? [...quotation.products] : [{ style_number: '', customisation_notes: '', quantity: 1 }],
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await quotationsApi.update(id, editData);
      toast.success('Quotation updated');
      setEditing(false);
      fetchQuotation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addEditProductRow = () => {
    setEditData(prev => ({
      ...prev,
      products: [...prev.products, { style_number: '', customisation_notes: '', quantity: 1 }]
    }));
  };

  const updateEditProduct = (idx, field, value) => {
    setEditData(prev => ({
      ...prev,
      products: prev.products.map((p, i) => i === idx ? { ...p, [field]: value } : p)
    }));
  };

  const removeEditProduct = (idx) => {
    if (editData.products.length <= 1) return;
    setEditData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== idx)
    }));
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-[1200px] mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!quotation) return null;

  const sCfg = statusConfig[quotation.status] || statusConfig['Pending'];
  const SIcon = sCfg.icon;
  const totalPcs = quotation.products.reduce((sum, p) => sum + (p.quantity || 0), 0);
  const currentStepIdx = statusSteps.indexOf(quotation.status);
  const isTerminal = quotation.status === 'Rejected' || quotation.status === 'Order Raised';

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="quotation-detail-page">
      {/* Header */}
      <div className="flex items-center gap-2 md:gap-3 mb-6">
        <HomeButton />
        <Button variant="ghost" size="icon" onClick={() => navigate('/quotations')} data-testid="back-to-quotations">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-heading font-bold text-slate-900 dark:text-white">
              {quotation.customer_name}
            </h1>
            <Badge variant="secondary" className={cn("flex items-center gap-1", sCfg.color)}>
              <SIcon className="h-3.5 w-3.5" />
              {quotation.status}
            </Badge>
            <Badge variant="secondary" className={cn(urgencyConfig[quotation.urgency])}>
              {quotation.urgency}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {quotation.requested_by && <span>Requested by <span className="font-medium text-slate-700 dark:text-slate-300">{quotation.requested_by}</span> | </span>}
            Created {formatDate(quotation.created_at)}
            {quotation.saleshub_reference_id && ` | Ref: ${quotation.saleshub_reference_id}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!editing && !isTerminal && (
            <Button variant="outline" size="sm" onClick={startEditing} data-testid="edit-quotation-btn">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
          {quotation.status !== 'Order Raised' && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-500 hover:text-red-600 border-red-200 hover:border-red-300 hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10"
              onClick={handleDelete}
              disabled={actionLoading === 'delete'}
              data-testid="delete-quotation-btn"
            >
              {actionLoading === 'delete' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Status Progress */}
      {!isTerminal && quotation.status !== 'Rejected' && (
        <Card className="mb-6 border-slate-200 dark:border-neutral-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-1">
              {statusSteps.map((step, idx) => {
                const isActive = idx === currentStepIdx;
                const isPast = idx < currentStepIdx;
                const StepCfg = statusConfig[step];
                return (
                  <React.Fragment key={step}>
                    <div className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                      isActive ? StepCfg.color : isPast
                        ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400"
                        : "bg-slate-100 text-slate-400 dark:bg-neutral-800 dark:text-slate-500"
                    )}>
                      {isPast ? <CheckCircle2 className="h-3 w-3" /> : <StepCfg.icon className="h-3 w-3" />}
                      <span className="hidden sm:inline">{step}</span>
                    </div>
                    {idx < statusSteps.length - 1 && (
                      <div className={cn(
                        "h-0.5 flex-1 min-w-2",
                        idx < currentStepIdx ? "bg-green-400 dark:bg-green-500" : "bg-slate-200 dark:bg-neutral-700"
                      )} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quotation Details */}
          <Card className="border-slate-200 dark:border-neutral-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Request Details</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Customer Name</Label>
                    <Input
                      value={editData.customer_name}
                      onChange={(e) => setEditData(prev => ({ ...prev, customer_name: e.target.value }))}
                      data-testid="edit-customer-name"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Diamond Quality</Label>
                      <Select value={editData.diamond_quality} onValueChange={(v) => setEditData(prev => ({ ...prev, diamond_quality: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {DIAMOND_QUALITIES.map(dq => <SelectItem key={dq} value={dq}>{dq}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Gold Karat</Label>
                      <Select value={editData.gold_karat} onValueChange={(v) => setEditData(prev => ({ ...prev, gold_karat: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {GOLD_KARATS.map(gk => <SelectItem key={gk} value={gk}>{gk}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Urgency</Label>
                      <Select value={editData.urgency} onValueChange={(v) => setEditData(prev => ({ ...prev, urgency: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {URGENCY_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Editable Products */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Products</Label>
                      <Button variant="outline" size="sm" onClick={addEditProductRow}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {editData.products.map((p, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <Input
                            className="flex-1"
                            placeholder="Style Number"
                            value={p.style_number}
                            onChange={(e) => updateEditProduct(idx, 'style_number', e.target.value)}
                          />
                          <Input
                            className="flex-1"
                            placeholder="Notes"
                            value={p.customisation_notes}
                            onChange={(e) => updateEditProduct(idx, 'customisation_notes', e.target.value)}
                          />
                          <Input
                            className="w-20"
                            type="number"
                            min="1"
                            value={p.quantity}
                            onChange={(e) => updateEditProduct(idx, 'quantity', parseInt(e.target.value) || 1)}
                          />
                          {editData.products.length > 1 && (
                            <Button variant="ghost" size="icon" className="text-red-500 h-9 w-9" onClick={() => removeEditProduct(idx)}>
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Special Instructions</Label>
                    <Textarea
                      value={editData.special_instructions}
                      onChange={(e) => setEditData(prev => ({ ...prev, special_instructions: e.target.value }))}
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea
                      value={editData.internal_notes}
                      onChange={(e) => setEditData(prev => ({ ...prev, internal_notes: e.target.value }))}
                      rows={2}
                      placeholder="Notes visible only to the team..."
                    />
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-quotation-btn">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Diamond Quality</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{quotation.diamond_quality || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Gold Karat</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{quotation.gold_karat || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Total Pieces</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{totalPcs}</p>
                    </div>
                  </div>

                  {quotation.special_instructions && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Special Instructions</p>
                      <p className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-neutral-900 p-3 rounded-lg">
                        {quotation.special_instructions}
                      </p>
                    </div>
                  )}

                  {quotation.internal_notes && (
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Internal Notes</p>
                      <p className="text-sm text-slate-700 dark:text-slate-300 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-lg border border-amber-200 dark:border-amber-500/20">
                        {quotation.internal_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Products Table */}
          <Card className="border-slate-200 dark:border-neutral-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Products ({quotation.products.length} styles, {totalPcs} pcs)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-neutral-800 bg-slate-50/50 dark:bg-neutral-900/50">
                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-400">#</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-400">Style Number</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-400">Customisation Notes</th>
                      <th className="text-right px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-400">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotation.products.map((p, idx) => (
                      <tr key={idx} className="border-b border-slate-100 dark:border-neutral-800/50 last:border-0">
                        <td className="px-4 py-3 text-slate-400">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{p.style_number}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{p.customisation_notes || '-'}</td>
                        <td className="px-4 py-3 text-right font-medium">{p.quantity}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50/50 dark:bg-neutral-900/50">
                      <td colSpan="3" className="px-4 py-2.5 font-semibold text-slate-700 dark:text-slate-300 text-right">Total</td>
                      <td className="px-4 py-2.5 font-bold text-right text-slate-900 dark:text-white">{totalPcs}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* File Upload / Download */}
          <Card className="border-slate-200 dark:border-neutral-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Quotation Document</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quotation.quotation_file_name ? (
                <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg border border-emerald-200 dark:border-emerald-500/20">
                  <FileText className="h-8 w-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{quotation.quotation_file_name}</p>
                    {quotation.prepared_by && (
                      <p className="text-xs text-slate-500">Prepared by {quotation.prepared_by}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 border-2 border-dashed border-slate-200 dark:border-neutral-700 rounded-lg text-center">
                  <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">No document uploaded yet</p>
                </div>
              )}

              <div className="flex gap-2">
                {!isTerminal && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      onChange={handleUpload}
                      data-testid="file-upload-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-quotation-btn"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {quotation.quotation_file_name ? 'Replace' : 'Upload'}
                    </Button>
                  </>
                )}
                {quotation.quotation_file_name && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={handleDownload}
                    disabled={downloading}
                    data-testid="download-quotation-btn"
                  >
                    {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                    Download
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {!isTerminal && (
            <Card className="border-slate-200 dark:border-neutral-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* Status transitions */}
                {quotation.status === 'Pending' && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => handleStatusChange('In Progress')}
                    disabled={!!actionLoading}
                    data-testid="action-in-progress"
                  >
                    {actionLoading === 'In Progress' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Loader2 className="h-4 w-4 mr-2" />}
                    Start Working
                  </Button>
                )}

                {(quotation.status === 'Quotation Ready' || quotation.status === 'In Progress') && !quotation.quotation_file_name && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Upload the quotation document to proceed
                  </p>
                )}

                {quotation.status === 'Quotation Ready' && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => handleStatusChange('Sent to Customer')}
                    disabled={!!actionLoading}
                    data-testid="action-sent-to-customer"
                  >
                    {actionLoading === 'Sent to Customer' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Mark Sent to Customer
                  </Button>
                )}

                {quotation.status === 'Sent to Customer' && (
                  <>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      onClick={handleConfirm}
                      disabled={!!actionLoading}
                      data-testid="action-confirm"
                    >
                      {actionLoading === 'confirm' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Customer Confirmed
                    </Button>
                    <Button
                      className="w-full"
                      variant="destructive"
                      onClick={handleReject}
                      disabled={!!actionLoading}
                      data-testid="action-reject"
                    >
                      {actionLoading === 'reject' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                      Customer Rejected
                    </Button>
                  </>
                )}

                {quotation.status === 'Confirmed' && !quotation.order_id && (
                  <Button
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleCreateOrder}
                    disabled={!!actionLoading}
                    data-testid="action-create-order"
                  >
                    {actionLoading === 'create-order' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
                    Raise Production Order
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Webhook Info for SalesHub integration */}
          <Card className="border-slate-200 dark:border-neutral-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Integration Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-xs">
                <div>
                  <p className="text-slate-500 dark:text-slate-400">Quotation ID</p>
                  <p className="font-mono text-slate-700 dark:text-slate-300 break-all">{quotation.id}</p>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-slate-400">Requested By</p>
                  <p className="font-medium text-slate-700 dark:text-slate-300">{quotation.requested_by || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-slate-400">Source</p>
                  <p className="font-medium text-slate-700 dark:text-slate-300 capitalize">{quotation.source === 'saleshub' ? 'SalesHub' : 'Manual'}</p>
                </div>
                {quotation.saleshub_reference_id && (
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">SalesHub Reference</p>
                    <p className="font-medium text-slate-700 dark:text-slate-300">{quotation.saleshub_reference_id}</p>
                  </div>
                )}
                <div>
                  <p className="text-slate-500 dark:text-slate-400">Last Updated</p>
                  <p className="font-medium text-slate-700 dark:text-slate-300">{formatDate(quotation.updated_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
